/**
 * Part 2B — Auto-Calibrated BIA Impact Thresholds.
 *
 * Calls POST /api/bia-configuration/ai-seed and renders a side-by-side
 * comparison of the GENERIC baseline vs the thresholds CALIBRATED to this
 * organization's revenue band, employee count, sector and frameworks.
 */
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Scale, TrendingUp } from "lucide-react";

function fmtUsd(n) {
  if (n === null || n === undefined) return null;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
}

function levelText(lvl) {
  if (!lvl) return "—";
  const parts = [];
  if (lvl.min_usd != null || lvl.max_usd != null) {
    const lo = fmtUsd(lvl.min_usd);
    const hi = fmtUsd(lvl.max_usd);
    if (lo && hi) parts.push(`${lo}–${hi}`);
    else if (hi) parts.push(`< ${hi}`);
    else if (lo) parts.push(`≥ ${lo}`);
  }
  if (lvl.min_hours != null || lvl.max_hours != null) {
    const lo = lvl.min_hours, hi = lvl.max_hours;
    if (lo != null && hi != null) parts.push(`${lo}–${hi}h`);
    else if (hi != null) parts.push(`< ${hi}h`);
  }
  if (lvl.criteria) parts.push(lvl.criteria);
  return parts.join(" · ") || "—";
}

function ThresholdTable({ rows, accent }) {
  if (!rows || rows.length === 0) return <p className="text-xs text-slate-400 italic">No thresholds.</p>;
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="rounded-lg border border-slate-100 p-2.5">
          <p className={`text-xs font-semibold ${accent} mb-1`}>{r.category}</p>
          <div className="grid grid-cols-1 gap-1 text-[11px]">
            <div className="flex gap-2"><span className="w-14 shrink-0 font-medium text-red-600">High</span><span className="text-slate-600">{levelText(r.high)}</span></div>
            <div className="flex gap-2"><span className="w-14 shrink-0 font-medium text-amber-600">Medium</span><span className="text-slate-600">{levelText(r.medium)}</span></div>
            <div className="flex gap-2"><span className="w-14 shrink-0 font-medium text-emerald-600">Low</span><span className="text-slate-600">{levelText(r.low)}</span></div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function BIAThresholdCalibration({ orgId: orgIdProp }) {
  const { api, user } = useAuth();
  const orgId = orgIdProp || user?.org_id;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.post("/bia-configuration/ai-seed", { org_id: orgId });
      setData(res.data);
      if (res.data?._ai_source && res.data._ai_source !== "gpt-4o") {
        toast.info("Showing deterministic calibration (AI unavailable).");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to calibrate thresholds");
    } finally {
      setLoading(false);
    }
  };

  const basis = data?.calibration_basis || {};

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4" data-testid="bia-threshold-calibration">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Scale className="w-4 h-4 text-emerald-700" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">Auto-Calibrate Impact Thresholds</p>
            <p className="text-[11px] text-emerald-700/80">
              Scale dollar amounts &amp; downtime tiers to your revenue band, size and frameworks.
            </p>
          </div>
        </div>
        <Button size="sm" onClick={load} disabled={loading} data-testid="bia-calibrate-btn"
          className="bg-emerald-600 hover:bg-emerald-700">
          {loading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
          {data ? "Recalibrate" : "Calibrate for My Org"}
        </Button>
      </div>

      {data && (
        <div className="mt-4 space-y-3" data-testid="bia-calibration-results">
          {/* Calibration basis */}
          <div className="flex flex-wrap items-center gap-2">
            {basis.sector && <Badge className="bg-white text-slate-600 border border-slate-200">{basis.sector}</Badge>}
            {basis.revenue_band && <Badge className="bg-white text-slate-600 border border-slate-200">Revenue: {basis.revenue_band}</Badge>}
            {basis.employee_count && <Badge className="bg-white text-slate-600 border border-slate-200">{basis.employee_count} employees</Badge>}
            {(basis.frameworks || []).slice(0, 3).map((f) => (
              <Badge key={f} className="bg-white text-slate-600 border border-slate-200">{f}</Badge>
            ))}
          </div>

          {/* Side-by-side comparison */}
          <div className="grid md:grid-cols-2 gap-3">
            <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="generic-baseline-col">
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Generic Baseline</span>
              </div>
              <ThresholdTable rows={data.generic_baseline?.impact_thresholds} accent="text-slate-600" />
            </div>
            <div className="rounded-lg bg-white border-2 border-emerald-300 p-3 shadow-sm" data-testid="calibrated-col">
              <div className="flex items-center gap-1.5 mb-2">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Calibrated for You</span>
              </div>
              <ThresholdTable rows={data.impact_thresholds} accent="text-emerald-700" />
            </div>
          </div>

          {data.reasoning && (
            <p className="text-[11px] text-slate-500 bg-white rounded-md border border-slate-100 p-2">{data.reasoning}</p>
          )}
        </div>
      )}
    </div>
  );
}
